import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import type { SavedCFSearch, CFQuery } from '../types/campaignFinance';

interface UseCFSavedSearchesReturn {
  savedSearches: SavedCFSearch[];
  loading: boolean;
  save: (name: string, query: CFQuery) => Promise<SavedCFSearch | null>;
  remove: (id: string) => Promise<void>;
  updateRun: (id: string, resultCount: number) => Promise<void>;
  refresh: () => void;
}

export function useCFSavedSearches(): UseCFSavedSearchesReturn {
  const [savedSearches, setSavedSearches] = useState<SavedCFSearch[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function fetch() {
      setLoading(true);
      try {
        const res = await api.get('/cf-saved-searches');
        if (!cancelled && res.data?.success) {
          setSavedSearches(res.data.data || []);
        }
      } catch {
        // endpoint may not exist yet
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetch();
    return () => { cancelled = true; };
  }, [refreshKey]);

  const save = useCallback(async (name: string, query: CFQuery): Promise<SavedCFSearch | null> => {
    try {
      const { searchQuery, scope, ...filters } = query;
      const res = await api.post('/cf-saved-searches', {
        name,
        filters,
        searchQuery: searchQuery || '',
        scope: scope || 'any',
      });
      if (res.data?.success) {
        setRefreshKey(k => k + 1);
        return res.data.data;
      }
    } catch (err: any) {
      if (err.response?.status === 409) {
        throw new Error('A search with this name already exists');
      }
      throw err;
    }
    return null;
  }, []);

  const remove = useCallback(async (id: string) => {
    await api.delete(`/cf-saved-searches/${id}`);
    setRefreshKey(k => k + 1);
  }, []);

  const updateRun = useCallback(async (id: string, resultCount: number) => {
    try {
      await api.patch(`/cf-saved-searches/${id}/run`, { resultCount });
    } catch {
      // non-critical
    }
  }, []);

  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

  return { savedSearches, loading, save, remove, updateRun, refresh };
}
